export default class Footer {
	constructor() {
		this.footerCanvas = document.querySelector('#footer-canvas')

		if (!this.footerCanvas) return

		this.ctx = this.footerCanvas.getContext('2d')
		this.triangles = []
		this.fallingTriangles = []
		this.animating = false
		this.borderColor = '#d98894'
		this.fillColor = '#B1122A'
		this.cellSize = 0
		this.isMobile = window.innerWidth < 768
		this.scrollTriggered = false
		this.scrollDropIndex = 0
		this.scrollDropTimer = null

		this.#init()
	}

	#init() {
		this.#resize()
		this.#buildGrid()
		this.#draw()
		this.#addEventlisteners()
	}

	#resize() {
		const wrapper = this.footerCanvas.parentElement
		const rect = wrapper.getBoundingClientRect()
		const dpr = window.devicePixelRatio || 1

		// Calculate cell size and snap wrapper height to exact row count
		const targetCols = Math.max(3, Math.round(rect.width / 100))
		const cellSize = rect.width / targetCols
		const rows = Math.max(1, Math.round(rect.height / cellSize))
		const snappedHeight = rows * cellSize

		wrapper.style.height = snappedHeight + 'px'
		wrapper.style.boxSizing = 'border-box'

		this.footerCanvas.width = rect.width * dpr
		this.footerCanvas.height = snappedHeight * dpr
		this.footerCanvas.style.width = rect.width + 'px'
		this.footerCanvas.style.height = snappedHeight + 'px'
		this.ctx.scale(dpr, dpr)
		this.width = rect.width
		this.height = snappedHeight
	}

	#buildGrid() {
		this.triangles = []

		// Determine cell size so squares fill the canvas nicely
		const targetCols = Math.max(3, Math.round(this.width / 100))
		this.cellSize = this.width / targetCols
		const cols = targetCols
		const rows = Math.ceil(this.height / this.cellSize) + 1

		for (let row = 0; row < rows; row++) {
			for (let col = 0; col < cols; col++) {
				const x = col * this.cellSize
				const y = row * this.cellSize
				const s = this.cellSize

				// Alternating diagonal direction for the pattern
				const even = (row + col) % 2 === 0

				const x2 = x + s
				const y2 = y + s

				if (even) {
					this.triangles.push({
						points: [
							{ x: x, y: y },
							{ x: x2, y: y },
							{ x: x, y: y2 },
						],
						row: row,
						col: col,
						fallen: false,
					})
					this.triangles.push({
						points: [
							{ x: x2, y: y },
							{ x: x2, y: y2 },
							{ x: x, y: y2 },
						],
						row: row,
						col: col,
						fallen: false,
					})
				} else {
					this.triangles.push({
						points: [
							{ x: x, y: y },
							{ x: x2, y: y },
							{ x: x2, y: y2 },
						],
						row: row,
						col: col,
						fallen: false,
					})
					this.triangles.push({
						points: [
							{ x: x, y: y },
							{ x: x2, y: y2 },
							{ x: x, y: y2 },
						],
						row: row,
						col: col,
						fallen: false,
					})
				}
			}
		}

		// Shuffle order for random scroll-triggered falling
		this.shuffledIndices = this.triangles.map((_, i) => i)
		for (let i = this.shuffledIndices.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1))
			const temp = this.shuffledIndices[i]
			this.shuffledIndices[i] = this.shuffledIndices[j]
			this.shuffledIndices[j] = temp
		}
		this.scrollDropIndex = 0
	}

	#draw() {
		this.ctx.clearRect(0, 0, this.width, this.height)

		for (const tri of this.triangles) {
			if (tri.fallen) continue
			this.#drawTriangle(tri.points, 0, 0, 0)
		}

		for (const ft of this.fallingTriangles) {
			this.#drawTriangle(ft.points, ft.offsetX, ft.offsetY, ft.rotation, ft.cx, ft.cy)
		}
	}

	#drawTriangle(points, offsetX, offsetY, rotation, cx, cy) {
		this.ctx.save()

		if (rotation && cx !== undefined) {
			this.ctx.translate(cx + offsetX, cy + offsetY)
			this.ctx.rotate(rotation)
			this.ctx.translate(-(cx + offsetX), -(cy + offsetY))
		}

		this.ctx.beginPath()
		this.ctx.moveTo(points[0].x + offsetX, points[0].y + offsetY)
		this.ctx.lineTo(points[1].x + offsetX, points[1].y + offsetY)
		this.ctx.lineTo(points[2].x + offsetX, points[2].y + offsetY)
		this.ctx.closePath()

		this.ctx.fillStyle = this.fillColor
		this.ctx.fill()

		this.ctx.strokeStyle = this.borderColor
		this.ctx.lineWidth = 1
		this.ctx.stroke()

		this.ctx.restore()
	}

	#addEventlisteners() {
		// Desktop: mousemove triggers falling
		if (!this.isMobile) {
			this.footerCanvas.addEventListener('mousemove', e => this.#handleMouseMovement(e))
		}

		// Mobile: IntersectionObserver triggers falling on scroll into view
		if (this.isMobile) {
			this.observer = new IntersectionObserver(
				entries => {
					for (const entry of entries) {
						if (entry.isIntersecting && !this.scrollTriggered) {
							this.scrollTriggered = true
							this.#startScrollDrop()
						}
					}
				},
				{ threshold: 0.3 },
			)
			this.observer.observe(this.footerCanvas)
		}

		window.addEventListener('resize', () => {
			const wasMobile = this.isMobile
			this.isMobile = window.innerWidth < 768

			this.#resize()
			this.#buildGrid()
			this.#draw()

			// Reset scroll state on resize
			this.scrollTriggered = false
			if (this.scrollDropTimer) {
				clearInterval(this.scrollDropTimer)
				this.scrollDropTimer = null
			}

			// Rebind if device type changed
			if (wasMobile !== this.isMobile) {
				this.#rebindInteraction(wasMobile)
			}
		})
	}

	#rebindInteraction(wasMobile) {
		if (wasMobile && !this.isMobile) {
			// Switched to desktop — add mousemove, remove observer
			this.footerCanvas.addEventListener('mousemove', e => this.#handleMouseMovement(e))
			if (this.observer) {
				this.observer.disconnect()
				this.observer = null
			}
		} else if (!wasMobile && this.isMobile) {
			// Switched to mobile — add observer
			this.observer = new IntersectionObserver(
				entries => {
					for (const entry of entries) {
						if (entry.isIntersecting && !this.scrollTriggered) {
							this.scrollTriggered = true
							this.#startScrollDrop()
						}
					}
				},
				{ threshold: 0.3 },
			)
			this.observer.observe(this.footerCanvas)
		}
	}

	#startScrollDrop() {
		const dropBatch = 3
		const intervalMs = 60

		this.scrollDropTimer = setInterval(() => {
			let dropped = 0
			while (dropped < dropBatch && this.scrollDropIndex < this.shuffledIndices.length) {
				const idx = this.shuffledIndices[this.scrollDropIndex]
				const tri = this.triangles[idx]
				this.scrollDropIndex++

				if (tri.fallen) continue
				this.#dropTriangle(tri)
				dropped++
			}

			if (!this.animating && dropped > 0) {
				this.animating = true
				this.#animate()
			}

			if (this.scrollDropIndex >= this.shuffledIndices.length) {
				clearInterval(this.scrollDropTimer)
				this.scrollDropTimer = null
			}
		}, intervalMs)
	}

	#dropTriangle(tri) {
		tri.fallen = true

		const cx = (tri.points[0].x + tri.points[1].x + tri.points[2].x) / 3
		const cy = (tri.points[0].y + tri.points[1].y + tri.points[2].y) / 3

		this.fallingTriangles.push({
			points: tri.points,
			offsetX: 0,
			offsetY: 0,
			velocityX: (Math.random() - 0.5) * 2,
			velocityY: Math.random() * 2 + 1,
			rotation: 0,
			rotationSpeed: (Math.random() - 0.5) * 0.05,
			cx,
			cy,
			gravity: 0.3,
		})
	}

	#handleMouseMovement(e) {
		const rect = this.footerCanvas.getBoundingClientRect()
		const mx = e.clientX - rect.left
		const my = e.clientY - rect.top

		for (let i = this.triangles.length - 1; i >= 0; i--) {
			const tri = this.triangles[i]
			if (tri.fallen) continue

			if (this.#isPointInTriangle(mx, my, tri.points)) {
				this.#dropTriangle(tri)

				if (!this.animating) {
					this.animating = true
					this.#animate()
				}
			}
		}
	}

	#isPointInTriangle(px, py, points) {
		const [a, b, c] = points
		const v0x = c.x - a.x
		const v0y = c.y - a.y
		const v1x = b.x - a.x
		const v1y = b.y - a.y
		const v2x = px - a.x
		const v2y = py - a.y

		const dot00 = v0x * v0x + v0y * v0y
		const dot01 = v0x * v1x + v0y * v1y
		const dot02 = v0x * v2x + v0y * v2y
		const dot11 = v1x * v1x + v1y * v1y
		const dot12 = v1x * v2x + v1y * v2y

		const inv = 1 / (dot00 * dot11 - dot01 * dot01)
		const u = (dot11 * dot02 - dot01 * dot12) * inv
		const v = (dot00 * dot12 - dot01 * dot02) * inv

		return u >= 0 && v >= 0 && u + v <= 1
	}

	#animate() {
		for (let i = this.fallingTriangles.length - 1; i >= 0; i--) {
			const ft = this.fallingTriangles[i]
			ft.velocityY += ft.gravity
			ft.offsetX += ft.velocityX
			ft.offsetY += ft.velocityY
			ft.rotation += ft.rotationSpeed

			const maxY = Math.max(ft.points[0].y, ft.points[1].y, ft.points[2].y)
			if (ft.offsetY + maxY > this.height + this.cellSize) {
				this.fallingTriangles.splice(i, 1)
			}
		}

		this.#draw()

		if (this.fallingTriangles.length > 0 || this.scrollDropTimer) {
			requestAnimationFrame(() => this.#animate())
		} else {
			this.animating = false
		}
	}
}
